import React from "react";
import { CheckCircle2, Info, Paperclip, Upload, X } from "lucide-react";
import { toast } from "../../utils/toast";
import translations from "./Translations";

// Optional "Attach Proof" picker shared by the Interactive RC / Special RC tabs
// and the account protection modal. Markup is kept identical to the block in
// CreateInstallment.jsx so every screen renders the same control.
//
// `existingUrl` is for edit flows: it shows the already-saved document with a
// link to open it, and lets the user drop it. Picking a new file replaces it.
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

const AttachProofInput = ({
  file,
  onChange,
  theme,
  langMode,
  existingUrl,
  onRemoveExisting,
}) => {
  const isRtl = langMode === "ar" || langMode === "ur";

  // Type filtering is frontend-only: the backend takes the uploaded URL as a
  // plain string and does not re-check the file type.
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      toast.error(translations[langMode].onlypdfimgallowed);
      e.target.value = ""; // clears the file input
      onChange(null);
      return;
    }

    onChange(selected);
  };

  return (
    <div className="flex flex-col relative">
      <label className="text-sm font-semibold mb-1 ml-2">
        {translations[langMode].attachProof || "Attach Proof (PDF/Image)"}
      </label>
      <div
        className={`relative w-full min-h-[50px] flex items-center overflow-hidden rounded-lg border px-3 transition-colors focus-within:border-blue-500 focus-within:outline-none ${
          theme === "dark"
            ? "border-gray-600 bg-[#0f172a]"
            : theme === "high-contrast"
              ? "border-black bg-white"
              : "border-gray-300 bg-white"
        }`}
      >
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={handleFileChange}
          className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
        />
        <div
          className={`relative z-10 flex w-full items-center justify-between gap-3 ${
            isRtl ? "flex-row-reverse text-right" : ""
          }`}
        >
          <div className={`flex min-w-0 items-center gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]">
              <Upload size={15} />
            </span>
            <span className="inline-flex shrink-0 rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-semibold text-white sm:text-sm">
              {translations[langMode]?.chooseFile ?? "Choose File"}
            </span>
            <span
              className={`truncate text-xs sm:text-sm ${
                theme === "dark" ? "text-gray-200" : "text-gray-600"
              }`}
            >
              {file?.name ||
                (existingUrl
                  ? translations[langMode]?.currentAttachment ?? "Current document attached"
                  : translations[langMode]?.noFile ?? "No file chosen")}
            </span>
          </div>
          <div className={`shrink-0 text-gray-400 ${theme === "dark" ? "text-gray-300" : ""}`}>
            <Paperclip size={16} />
          </div>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500 inline-flex items-center gap-1.5">
        <Upload size={12} />
        PDF, JPG, JPEG, PNG
      </p>
      {/* Retention notice: the file is kept against the account and surfaced
          again later, so the operator is told before they upload. */}
      <p className="mt-1 flex items-start gap-1.5 text-xs italic text-gray-500">
        <Info size={12} className="mt-[2px] shrink-0" />
        <span>
          {translations[langMode].attachProofNote ||
            "Note: The uploaded document is stored against this account and will be available to view later."}
        </span>
      </p>
      {/* Edit flow: show the saved document so the operator can open it, and
          let them drop it. Picking a new file replaces it, so this hides. */}
      {!file && existingUrl && (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
          <Paperclip size={12} className="shrink-0" />
          <a
            href={existingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-[#236DB4] underline"
          >
            {translations[langMode]?.viewAttachment ?? "View attached document"}
          </a>
          {onRemoveExisting && (
            <button
              type="button"
              onClick={onRemoveExisting}
              aria-label={translations[langMode]?.removeAttachment ?? "Remove attachment"}
              className="inline-flex shrink-0 items-center justify-center text-gray-400 hover:text-red-500"
            >
              <X size={13} />
            </button>
          )}
        </p>
      )}
      {file && (
        <p className="text-xs mt-1 text-green-600 inline-flex items-center gap-1.5">
          <CheckCircle2 size={12} />
          {file.name} selected
        </p>
      )}
    </div>
  );
};

export default AttachProofInput;
